// =============================================================================
// features/policies/api.ts — Cliente HTTP de ciência de política interna
// (F51-S17).
//
// Endpoints (backend F51-S17, apps/api/src/modules/policies/routes.ts):
//   GET  /api/policies/:key/status                — minha ciência + versão vigente
//   POST /api/policies/:key/accept                  — confirmar ciência da vigente
//   POST /api/policies/:key/versions                 — admin: cadastra nova versão
//   GET  /api/policies/:key/acceptances               — admin: quem aceitou
//   GET  /api/policies/:key/acceptances/export         — admin: CSV (blob)
//
// Único ponto de acesso à rede da feature — nunca useEffect+fetch em
// componentes.
// =============================================================================
import type {
  AcceptPolicyResponse,
  CreatePolicyVersionBody,
  PolicyAcceptanceListResponse,
  PolicyStatusResponse,
} from '@elemento/shared-schemas';

import { api } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';

// ---------------------------------------------------------------------------
// Autoatendimento — status + aceite
// ---------------------------------------------------------------------------

/** GET /api/policies/:key/status — versão vigente + minha ciência. */
export async function fetchPolicyStatus(policyKey: string): Promise<PolicyStatusResponse> {
  return api.get<PolicyStatusResponse>(`/api/policies/${encodeURIComponent(policyKey)}/status`);
}

/** POST /api/policies/:key/accept — confirma ciência da versão vigente. */
export async function acceptPolicy(policyKey: string): Promise<AcceptPolicyResponse> {
  return api.post<AcceptPolicyResponse>(
    `/api/policies/${encodeURIComponent(policyKey)}/accept`,
    {},
  );
}

// ---------------------------------------------------------------------------
// Admin — policies:manage
// ---------------------------------------------------------------------------

/** POST /api/policies/:key/versions — cadastra nova versão do texto (reabre o aceite de todos). */
export async function createPolicyVersion(
  policyKey: string,
  body: CreatePolicyVersionBody,
): Promise<PolicyStatusResponse['document']> {
  return api.post<PolicyStatusResponse['document']>(
    `/api/policies/${encodeURIComponent(policyKey)}/versions`,
    body,
  );
}

/** GET /api/policies/:key/acceptances — lista paginada de quem aceitou. */
export async function fetchPolicyAcceptances(
  policyKey: string,
  params: { cursor?: string; limit?: number } = {},
): Promise<PolicyAcceptanceListResponse> {
  const qs = new URLSearchParams();
  if (params.cursor !== undefined) qs.set('cursor', params.cursor);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return api.get<PolicyAcceptanceListResponse>(
    `/api/policies/${encodeURIComponent(policyKey)}/acceptances${query ? `?${query}` : ''}`,
  );
}

// ---------------------------------------------------------------------------
// Export CSV — resposta binária, mesmo padrão de features/relatorios/api.ts
// (postReportsExport): fetch direto (não passa por lib/api.ts, que só devolve
// JSON), extrai filename do Content-Disposition.
// ---------------------------------------------------------------------------

const API_BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:3333';

function getCsrfToken(): string {
  const match = document.cookie.split('; ').find((row) => row.startsWith('csrf_token='));
  return match ? (match.split('=')[1] ?? '') : '';
}

export interface PolicyAcceptancesExportResult {
  blob: Blob;
  filename: string;
}

/** GET /api/policies/:key/acceptances/export — CSV (UTF-8 com BOM) para o RH. */
export async function exportPolicyAcceptances(
  policyKey: string,
): Promise<PolicyAcceptancesExportResult> {
  const accessToken = useAuthStore.getState().accessToken;
  const csrf = getCsrfToken();

  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  if (csrf) headers['X-CSRF-Token'] = csrf;

  const res = await fetch(
    `${API_BASE}/api/policies/${encodeURIComponent(policyKey)}/acceptances/export`,
    { method: 'GET', headers, credentials: 'include' },
  );

  if (!res.ok) {
    let message = `Erro ao exportar (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      // corpo não era JSON
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
  const filename = filenameMatch?.[1] ?? `aceites-${policyKey}.csv`;

  return { blob, filename };
}
